import { useEffect } from "react";
import PageHero from "@/components/PageHero";
import LocalizedLink from "@/components/LocalizedLink";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * Terms of Use
 *
 * Scoped to what the site actually does today: a free, non-profit educational
 * site with contact forms, free content (articles, tools directory, workshops
 * info), and an optional public user account. Kept plain-language on purpose.
 */

const C = {
  ar: {
    eyebrow: "الشروط",
    title: "شروط الاستخدام",
    lead: "باستخدامكم موقع مجتمع الذكاء الاصطناعي السوري (SAS)، فإنّكم توافقون على الشروط التالية. نحرص أن تكون بسيطة ومتوافقة مع طابع المبادرة التعليمي غير الربحي.",
    updatedLabel: "آخر تحديث",
    updatedDate: "نيسان/أبريل 2026",
    sections: [
      {
        heading: "طبيعة الموقع",
        body: [
          "SAS مبادرة تطوّعية غير ربحية. المحتوى والأدوات المعروضة على الموقع تهدف إلى التوعية والتعليم، وتُقدَّم مجاناً وبدون ضمانات صريحة أو ضمنية.",
          "قد نُشير إلى أدوات خارجية مجانية أو تُقدِّم خطط مجانية. استخدامكم لهذه الأدوات يخضع لشروطها الخاصّة المنشورة على مواقعها، ولا نتحمّل مسؤولية سياسات تلك الأدوات.",
        ],
      },
      {
        heading: "الاستخدام المقبول",
        body: [
          "يُرجى استخدام الموقع ونماذج التواصل لأغراض حقيقية تتعلق بالتعاون أو التعلّم أو الاستفسار. لا نسمح بإرسال رسائل عشوائية أو محتوى مضلّل أو إساءة للفريق أو المشاركين.",
          "قد نطبّق حدوداً معقولة على عدد الطلبات من الشبكة نفسها لحماية الخدمة من الاستخدام الآلي.",
        ],
      },
      {
        heading: "الحسابات",
        body: [
          "إذا أنشأت حساباً عاماً على الموقع، فأنت مسؤول عن سرّية كلمة المرور وعن أي نشاط يجري تحت حسابك. ننصح باستخدام كلمة مرور قوية وعدم مشاركتها.",
          "نحتفظ بالحق في تعليق أو حذف أي حساب يُستخدم بما يتعارض مع هذه الشروط.",
        ],
      },
      {
        heading: "الملكية الفكرية",
        body: [
          "المحتوى الأصلي على الموقع (النصوص، الترجمات، التصاميم، الهوية البصرية) مُلك لمجتمع الذكاء الاصطناعي السوري. يُسمح بالإشارة إلى المحتوى وإعادة نشر فقرات قصيرة مع نسبها إلى SAS.",
          "للنسخ الكامل أو إعادة الاستخدام الواسع، يُرجى التواصل معنا عبر نموذج التواصل العام.",
        ],
      },
      {
        heading: "حدود المسؤوليّة",
        body: [
          "نسعى لتقديم معلومات دقيقة ومحدّثة، لكن لا نضمن خلوّ المحتوى من الأخطاء أو ملاءمته لكل حالة. يتحمّل المستخدم مسؤوليّة قراراته المبنيّة على المحتوى أو الأدوات المُشار إليها.",
          "لا نتحمّل مسؤولية أي ضرر ناتج عن استخدام خدمات أو أدوات خارجيّة نذكرها على الموقع.",
        ],
      },
      {
        heading: "تعديل الشروط",
        body: [
          "قد نُحدّث هذه الشروط من وقت لآخر. يظهر تاريخ آخر تحديث أعلى الصفحة. استمراركم في استخدام الموقع بعد نشر التعديل يُعدّ موافقة على النسخة المحدّثة.",
        ],
      },
      {
        heading: "التواصل",
        body: [
          "لأي استفسار أو طلب متعلّق بهذه الشروط، يُرجى استخدام نماذج التواصل الداخليّة على الموقع.",
        ],
      },
    ],
    contactEyebrow: "لديكم سؤال حول الشروط؟",
    contactBody: "يمكنكم التواصل معنا عبر ",
    contactLink: "نموذج التواصل العام",
    contactAfter: " في أي وقت.",
    docTitle: "شروط الاستخدام · مجتمع الذكاء الاصطناعي السوري",
  },
  en: {
    eyebrow: "Terms",
    title: "Terms of Use",
    lead: "By using the Syrian AI Society (SAS) website, you agree to the following terms. We've kept them short and honest, to match the educational, non-profit nature of the initiative.",
    updatedLabel: "Last updated",
    updatedDate: "April 2026",
    sections: [
      {
        heading: "Nature of the site",
        body: [
          "SAS is a non-profit volunteer initiative. The content and tools presented on this site are for awareness and educational purposes, provided free of charge and without express or implied warranties.",
          "We may reference external free or free-tier tools. Your use of those tools is governed by their own terms published on their websites, and we are not responsible for the policies of those tools.",
        ],
      },
      {
        heading: "Acceptable use",
        body: [
          "Please use the site and contact forms for genuine collaboration, learning, or inquiry purposes. Spam, misleading content, and abuse of the team or participants are not allowed.",
          "We may apply reasonable limits on how many submissions originate from a single network in order to protect the service from automated abuse.",
        ],
      },
      {
        heading: "Accounts",
        body: [
          "If you create a public account, you are responsible for the confidentiality of your password and for all activity under your account. Use a strong password and do not share it.",
          "We reserve the right to suspend or delete any account used in a way that conflicts with these terms.",
        ],
      },
      {
        heading: "Intellectual property",
        body: [
          "Original content on the site (text, translations, designs, visual identity) belongs to the Syrian AI Society. Short quotations are welcome provided they are attributed to SAS.",
          "For full reproduction or broader reuse, please contact us via the General Contact form.",
        ],
      },
      {
        heading: "Limitation of liability",
        body: [
          "We aim to provide accurate and up-to-date information, but we do not guarantee the content is error-free or suitable for every situation. You are responsible for decisions you make based on the content or on external tools we reference.",
          "We are not liable for any damage resulting from the use of external services or tools mentioned on the site.",
        ],
      },
      {
        heading: "Changes to these terms",
        body: [
          "We may update these terms from time to time. The last-updated date above reflects the most recent revision. Your continued use of the site after an update constitutes acceptance of the updated version.",
        ],
      },
      {
        heading: "Contact",
        body: [
          "For any question regarding these terms, please use the internal contact forms on the site.",
        ],
      },
    ],
    contactEyebrow: "A question about these terms?",
    contactBody: "You can reach us through the ",
    contactLink: "General Contact form",
    contactAfter: " at any time.",
    docTitle: "Terms of Use · Syrian AI Society",
  },
};

export default function Terms() {
  const { lang } = useLanguage();
  const c = C[lang];

  useEffect(() => {
    const prev = document.title;
    document.title = c.docTitle;
    return () => { document.title = prev; };
  }, [c.docTitle]);

  return (
    <>
      <PageHero eyebrow={c.eyebrow} title={c.title} lead={c.lead} />
      <section className="sas-section" data-testid="terms-body">
        <div className="sas-container max-w-3xl">
          <p className="text-[12.5px] text-[color:var(--sas-ink-2)] mb-10">
            {c.updatedLabel}: <span className="font-medium text-[color:var(--sas-ink)]">{c.updatedDate}</span>
          </p>
          <div className="flex flex-col gap-10">
            {c.sections.map((s) => (
              <div key={s.heading}>
                <h2 className="sas-h3" data-testid="terms-section-heading">{s.heading}</h2>
                <div className="mt-3 flex flex-col gap-3 sas-body text-[15.5px] leading-[2]">
                  {s.body.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="sas-section-sm bg-[color:var(--sas-accent-soft)]/40 border-y border-[color:var(--sas-line)]" data-testid="terms-contact">
        <div className="sas-container max-w-3xl">
          <div className="sas-eyebrow mb-3">{c.contactEyebrow}</div>
          <p className="sas-body text-[15px]">
            {c.contactBody}
            <LocalizedLink to="/contact/general" className="sas-btn-link underline underline-offset-4" data-testid="terms-contact-link">
              {c.contactLink}
            </LocalizedLink>
            {c.contactAfter}
          </p>
        </div>
      </section>
    </>
  );
}
